import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import EmptyState from '../components/EmptyState'
import type { Incidencia, IncidenciasResponse, PasoTraza, Traza } from '../types/portal'

/**
 * "Trazas" — el recorrido completo de un caso, paso a paso.
 *
 * NO es un waterfall de X-Ray. El prototipo dibujaba spans con la duración de
 * cada salto (API Gateway 12 ms, Lambda 820 ms); eso necesita trazado
 * distribuido, que no está habilitado en ninguna de las 16 Lambdas. Se
 * reconstruye con los eventos que el sistema ya guarda: mensajes, documentos,
 * alertas del árbol y acciones del portal.
 *
 * Lo que se pierde: dónde está el cuello de botella de rendimiento.
 * Lo que se conserva: qué pasó, en qué orden, quién lo hizo y dónde se rompió
 * — que es la pregunta con la que se entra acá.
 *
 * Se llega desde el botón "Ver traza" de una incidencia, con el
 * `conversation_id` en la URL, o pegando el id a mano.
 */

const COLOR_TIPO: Record<PasoTraza['tipo'], string> = {
  mensaje:       'bg-brand',
  documento:     'bg-purple',
  alerta:        'bg-warn',
  sistema:       'bg-ink-faint',
  configuracion: 'bg-info',
}

const ETIQUETA_TIPO: Record<PasoTraza['tipo'], string> = {
  mensaje: 'Mensaje', documento: 'Documento', alerta: 'Alerta',
  sistema: 'Sistema', configuracion: 'Portal',
}

function hora(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleTimeString('es-PE', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

function fecha(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
}

function duracion(seg: number): string {
  if (seg < 60) return `${seg} s`
  if (seg < 3600) return `${Math.round(seg / 60)} min`
  const h = Math.floor(seg / 3600)
  return `${h} h ${Math.round((seg % 3600) / 60)} min`
}

export default function Traces() {
  const [params, setParams] = useSearchParams()
  const convDeUrl = params.get('conv') ?? ''

  const [entrada, setEntrada] = useState(convDeUrl)
  const [traza, setTraza] = useState<Traza | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Casos con incidencia abierta, para poder entrar sin conocer ningún ID.
  // Pedir un `conversation_id` a mano suponía que quien investiga ya sabe cuál
  // mirar; casi siempre la pregunta es al revés — "algo pasó, ¿dónde?".
  const [casos, setCasos] = useState<Incidencia[]>([])
  useEffect(() => {
    apiClient.get<IncidenciasResponse>('/incidents')
      .then((r) => setCasos((r.data.incidencias ?? [])
        .filter((i) => i.conversation_id && i.estado !== 'RESUELTA')
        .slice(0, 8)))
      .catch(() => { /* sin sugerencias; el buscador sigue sirviendo */ })
  }, [])

  const buscar = useCallback(async (convId: string) => {
    const id = convId.trim()
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.get<Traza>(`/traces/${encodeURIComponent(id)}`)
      setTraza(res.data)
    } catch (err) {
      setTraza(null)
      setError(errorMessage(err, 'No se pudo cargar la traza.'))
    } finally {
      setLoading(false)
    }
  }, [])

  // Llegar desde una incidencia carga la traza sola: obligar a pulsar "Buscar"
  // con el id ya en la URL sería un clic sin decisión.
  useEffect(() => {
    if (convDeUrl) void buscar(convDeUrl)
  }, [convDeUrl, buscar])

  return (
    <div className="flex flex-col gap-5">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => { e.preventDefault(); setParams({ conv: entrada.trim() }) }}
      >
        <input
          value={entrada}
          onChange={(e) => setEntrada(e.target.value)}
          placeholder="ID de conversación…"
          className="min-w-[320px] flex-1 rounded-input border border-surface-border px-3.5 py-2.5 font-mono text-body text-ink placeholder:font-sans placeholder:text-ink-faint focus:border-brand focus:outline-none"
        />
        <button
          type="submit"
          disabled={!entrada.trim() || loading}
          className="rounded-input bg-brand px-5 py-2.5 text-body font-bold text-white transition-colors hover:bg-brand-hover disabled:opacity-50"
        >
          {loading ? 'Buscando…' : 'Ver traza'}
        </button>
      </form>

      {error && (
        <p className="rounded-card border border-danger-border bg-danger-light px-4 py-3 text-body text-danger">
          {error}
        </p>
      )}

      {!traza && !loading && !error && (
        casos.length > 0 ? (
          <div className="rounded-card border border-surface-border bg-white">
            <div className="border-b border-surface-border px-5 py-3.5">
              <h3 className="text-[14px] font-extrabold text-ink">Casos con incidencia abierta</h3>
              <p className="mt-0.5 text-meta text-ink-muted">
                Son las conversaciones que ahora mismo tienen algo sin resolver.
              </p>
            </div>
            <ul className="divide-y divide-surface-faint">
              {casos.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setParams({ conv: c.conversation_id })}
                    className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-surface"
                  >
                    <span className={`shrink-0 rounded-chip border px-2 py-0.5 text-pill font-bold ${
                      c.severidad === 'CRITICA' || c.severidad === 'ALTA'
                        ? 'border-danger-border bg-danger-light text-danger'
                        : 'border-warn-border bg-warn-light text-warn'
                    }`}>
                      {c.severidad}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-chat font-semibold text-ink">{c.titulo}</span>
                      <span className="block truncate text-pill text-ink-muted">
                        {c.afectado} · {c.origen}
                      </span>
                    </span>
                    <span className="shrink-0 text-pill text-ink-faint">Ver traza →</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <EmptyState
            icon="🧭"
            title="Sin casos con incidencia"
            description="No hay conversaciones con algo sin resolver. Puedes pegar el ID de cualquiera para ver su recorrido."
          />
        )
      )}

      {traza?.encontrada && (
        <>
          {/* Cabecera: de quién es el caso y cómo terminó */}
          <div className="rounded-card border border-surface-border bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-[15px] font-extrabold text-ink">
                  {traza.cliente || 'Sin nombre'}
                </h2>
                <p className="mt-1 font-mono text-micro text-ink-muted">
                  {traza.phone} · {traza.conversation_id}
                </p>
              </div>
              <span
                className={`inline-flex items-center rounded-chip border px-3 py-1 text-pill font-bold ${
                  traza.fallos
                    ? 'border-danger-border bg-danger-light text-danger'
                    : 'border-brand-border bg-brand-light text-brand-hover'
                }`}
              >
                {traza.fallos
                  ? `${traza.fallos} paso${traza.fallos === 1 ? '' : 's'} con error`
                  : 'Sin errores'}
              </span>
            </div>
            <p className="mt-3 text-body text-ink-muted">
              <b className="text-ink">{traza.resumen}</b> · {traza.count} pasos ·
              {' '}{duracion(traza.duracion_seg)} de recorrido
              {traza.asignada_a && ` · atiende ${traza.asignada_a.split('@')[0]}`}
            </p>
            {traza.pasos_sin_hora > 0 && (
              <p className="mt-2 text-meta text-ink-faint">
                {traza.pasos_sin_hora} evento(s) sin marca de tiempo quedaron fuera:
                sin hora no se pueden ubicar en el orden real.
              </p>
            )}
          </div>

          {/* La línea de tiempo */}
          <div className="rounded-card border border-surface-border bg-white">
            <div className="border-b border-surface-border px-5 py-3.5">
              <h3 className="text-[14px] font-extrabold text-ink">Recorrido del caso</h3>
              <p className="mt-0.5 text-meta text-ink-muted">
                Reconstruido de los eventos guardados. No incluye la latencia por
                servicio: eso necesitaría trazado distribuido (X-Ray), que no está
                habilitado.
              </p>
            </div>
            <ol className="flex flex-col p-5 pl-6">
              {traza.pasos.map((p, i) => {
                const anterior = traza.pasos[i - 1]
                const nuevoDia = !anterior ||
                  new Date(anterior.timestamp).toDateString() !== new Date(p.timestamp).toDateString()
                return (
                  <li key={`${p.timestamp}-${i}`}>
                    {nuevoDia && (
                      <p className="mb-2 mt-3 text-meta font-bold uppercase tracking-[.06em] text-ink-faint first:mt-0">
                        {fecha(p.timestamp)}
                      </p>
                    )}
                    <div className="relative flex gap-3.5 pb-4">
                      {/* Riel vertical: se corta en el último paso para que la
                          línea no quede colgando en el vacío. */}
                      {i < traza.pasos.length - 1 && (
                        <span className="absolute left-[5px] top-4 h-full w-px bg-surface-border" aria-hidden />
                      )}
                      <span
                        className={`relative z-10 mt-1.5 h-[11px] w-[11px] shrink-0 rounded-full ring-2 ring-white ${
                          p.estado === 'error' ? 'bg-danger'
                            : p.estado === 'warn' ? 'bg-warn'
                            : COLOR_TIPO[p.tipo]
                        }`}
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                          <span className="font-mono text-micro tabular-nums text-ink-faint">
                            {hora(p.timestamp)}
                          </span>
                          <span className="text-chat font-bold text-ink">{p.titulo}</span>
                          <span className="rounded-chip bg-surface-subtle px-2 py-0.5 text-pill text-ink-muted">
                            {ETIQUETA_TIPO[p.tipo]}
                          </span>
                          {p.actor && (
                            <span className="text-pill text-ink-faint">{p.actor}</span>
                          )}
                        </div>
                        {p.detalle && (
                          <p className="mt-1 whitespace-pre-wrap break-words text-body text-ink-muted">
                            {p.detalle}
                          </p>
                        )}
                        {p.error && (
                          <p className="mt-1.5 rounded-btn border border-danger-border bg-danger-light px-2.5 py-1.5 font-mono text-micro text-danger">
                            {p.error}
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                )
              })}
            </ol>
          </div>
        </>
      )}
    </div>
  )
}
